/**
 * 8 職種の 60 秒の仕事体験 (C 案「触ってみて選ぶ」)。
 *
 * どの職種も同じ 2 段で進む。1 段目で原因の行を選び、2 段目で直し方を選ぶ。直し方が合っていれば、
 * 見本 (demo) が before から after に替わり、直したところ (diff) とこの仕事の毎日 (dayNote) を見せて、
 * 最後に「この仕事、もう少しやってみたい？」を聞く。形をそろえてあるのは、1 つの部品
 * (components/lab/job-diagnosis-trial-cta.tsx) がこの表を読むだけで、どの職種も描けるようにするため。
 *
 * picks の text と diff の text は画面に出すコードや設定の行で、半角のコロンを含んでよい。
 * それ以外の文 (headline、ask、say、label など) には半角のコロンを使わない (試験で見る)。
 * 「名前<TAB>値」の行は 2 列に、「番号<空白 2 つ>手順」の行は番号と手順の 2 列に並べる。
 *
 * 外れを選んだ数は誤りの数 (hintsUsed) として記録するが、順位には使わない。
 */
import type { RoleId } from "./job-type-diagnosis";

export type TrialPick = {
  /** 選べる行。改行を含んでよい */
  text: string;
  ok: boolean;
  /** 押した直後に出す一言。正解でも外れでも、なぜそうなのかを書く */
  say: string;
};

export type TrialFix = { label: string; ok: boolean; say: string };

export type TrialDiffLine = { text: string; mark: "del" | "add" | null };

type NewsRow = { date: string; text: string };
type BarsState = { badge: string; bars: { label: string; value: number }[] };
type MeterState = { value: number; max: number; note: string; tag: string };

/** 見本の見た目。kind ごとに、直す前 (before) と直したあと (after) を同じ形で持つ */
export type TrialDemo =
  | {
      /** お店のサイトの画面に置いた予約ボタン。CSS の指定をそのまま当てて、本当に折れる・直る */
      kind: "button";
      url: string;
      shop: string;
      label: string;
      /** 見本の外に添える、崩れている所の印 */
      brokenNote: string;
    }
  | { kind: "list"; title: string; before: NewsRow[]; after: NewsRow[] }
  | {
      kind: "browser";
      url: string;
      before: { bar: string; title: string; code?: string; text?: string };
      after: { bar: string; title: string; code?: string; text?: string };
    }
  | {
      kind: "chat";
      question: string;
      before: { answer: string; source: string };
      after: { answer: string; source: string };
    }
  | { kind: "bars"; title: string; unit: string; before: BarsState; after: BarsState }
  | { kind: "meter"; title: string; before: MeterState; after: MeterState }
  | {
      /** お店のトップの画像。CSS の高さの指定をそのまま当てて、本当に伸びる・直る */
      kind: "image";
      src: string;
      alt: string;
      before: { height: string };
      after: { height: string };
      /** 広い画面でも、縦と横の比のずれ方がスマホと同じになるように枠の幅を決める */
      frameMaxWidth: string;
    }
  | {
      kind: "table";
      columns: string[];
      before: string[][];
      after: string[][];
      highlightRow: number;
    };

export type TrialSpec = {
  id: RoleId;
  /** 職種の札。「バックエンドの仕事」など */
  kicker: string;
  /** 体験の題 */
  headline: string;
  /** 1 段目の問い */
  ask: string;
  /** 選べる行の欄の読み上げ名 */
  pickLabel: string;
  /** 選べる行の前後に置く、選べない行 */
  picksHead: string[];
  picks: TrialPick[];
  picksTail: string[];
  /** 2 段目の問い */
  fixAsk: string;
  fixes: TrialFix[];
  demo: TrialDemo;
  /** 直したところ。直したあとと、結果の「あなたがやったこと」に出す */
  diff: TrialDiffLine[];
  /** 「〇〇の毎日では、」で始まる、この仕事の毎日の一文 */
  dayNote: string;
};

export const TRIAL_INTEREST_QUESTION = "この仕事、もう少しやってみたい？";

export const TRIALS: Record<RoleId, TrialSpec> = {
  frontend: {
    id: "frontend",
    kicker: "フロントエンドの仕事",
    headline: "ボタンの言葉が途中で切れた",
    ask: "お店のサイトの予約ボタンが、スマホで言葉の途中から折れています。1行で見せたいとき、原因になっている行はどれ？",
    pickLabel: "ボタンの CSS。原因の行を選ぶ",
    picksHead: [".cta {"],
    picks: [
      {
        text: "  width: 120px;",
        ok: true,
        say: "正解。幅を120pxに決めているので、言葉が入りきらずに途中で折れます。",
      },
      {
        text: "  padding: 12px 0;",
        ok: false,
        say: "左右の余白は0なので、この行は幅を狭めていません。ボタンの幅を決めている行を探してみてください。",
      },
      {
        text: "  font-size: 16px;",
        ok: false,
        say: "文字の大きさも折れ方に関わります。ただ小さくしても、言葉が長くなればまた折れます。幅を決めている行を探してみてください。",
      },
    ],
    picksTail: ["}"],
    fixAsk: "では、どう直す？",
    fixes: [
      {
        label: "幅の指定をやめて、左右に余白をつける",
        ok: true,
        say: "正解。width をやめて padding: 12px 24px にすると、言葉の長さに合わせてボタンが広がります。画面がとても狭いときは、2行になることもあります。",
      },
      {
        label: "幅を200pxに広げる",
        ok: false,
        say: "今の言葉は1行に入りますが、言葉が長くなるとまた折れます。言葉の長さに合わせて広がる直し方があります。",
      },
      {
        label: "文字を小さくする",
        ok: false,
        say: "読みにくくなるうえ、言葉が長くなればまた折れます。幅の決め方を変えてみてください。",
      },
    ],
    demo: {
      kind: "button",
      url: "shop.example.jp",
      shop: "喫茶ふくろう",
      label: "この日の席を予約する",
      brokenNote: "予約ボタンの言葉が、途中で折れている",
    },
    diff: [
      { text: ".cta {", mark: null },
      { text: "  width: 120px;", mark: "del" },
      { text: "  padding: 12px 0;", mark: "del" },
      { text: "  padding: 12px 24px;", mark: "add" },
      { text: "}", mark: null },
    ],
    dayNote:
      "フロントエンドの毎日では、作った画面をスマホとパソコンで開いて確かめ、崩れた所を1つずつ直します。",
  },
  "backend": {
    "id": "backend",
    "kicker": "バックエンドの仕事",
    "headline": "新着のお知らせが、2年前のまま",
    "ask": "お店のアプリで最新のお知らせを3件出したいのに、2年前の記事ばかり並んでいます。ヒントとして、並べる向きを書かないと、日付は古い順に並びます。お知らせを取り出す命令 (SQL) のうち、原因の行はどれ？",
    "pickLabel": "お知らせを取り出す SQL。原因の行を選ぶ",
    "picksHead": [
      "SELECT title, posted_at\n  -- 題と日付を"
    ],
    "picks": [
      {
        "text": "FROM news\n  -- お知らせの表から",
        "ok": false,
        "say": "お知らせの表から取り出す、という行です。取り出す場所は合っています。問題は、どの3件を取るかです。"
      },
      {
        "text": "ORDER BY posted_at\n  -- 日付で並べて",
        "ok": true,
        "say": "正解。並べる向きを書かないと、日付の小さい順、つまり古い順になります。だから一番古い3件が上に来ます。"
      },
      {
        "text": "LIMIT 3;\n  -- 3件だけ取る",
        "ok": false,
        "say": "3件に絞る行です。何件出すかは合っています。問題は、どの順に並べてから3件を取っているかです。"
      }
    ],
    "picksTail": [],
    "fixAsk": "では、どう直す？",
    "fixes": [
      {
        "label": "受け取ったあと、アプリの側で新しい順に並べ替える",
        "ok": false,
        "say": "データベースが先に古い3件に絞ってしまうので、並べ替えても古い3件のままです。どの3件を取るかは、データベースの側で決めます。"
      },
      {
        "label": "並べる向きを、新しい順 (DESC) にする",
        "ok": true,
        "say": "正解。ORDER BY posted_at DESC にすると、新しい順に並べてから3件を取るので、最新の3件が返ります。「並べてから絞る」の順番が大事です。"
      },
      {
        "label": "LIMIT 3 を消して、全件を返す",
        "ok": false,
        "say": "全件が古い順で返るので、一番上は2024年のままです。お知らせが増えるほど、返すデータも重くなります。"
      }
    ],
    "demo": {
      "kind": "list",
      "title": "最新のお知らせ",
      "before": [
        {
          "date": "2024.04.01",
          "text": "サイトを開きました"
        },
        {
          "date": "2024.05.10",
          "text": "ご予約の受付を始めました"
        },
        {
          "date": "2024.06.03",
          "text": "駐車場のご案内"
        }
      ],
      "after": [
        {
          "date": "2026.10.12",
          "text": "秋の限定メニューを始めました"
        },
        {
          "date": "2026.09.01",
          "text": "9月の定休日のお知らせ"
        },
        {
          "date": "2025.12.20",
          "text": "年末年始の営業時間"
        }
      ]
    },
    "diff": [
      {
        "text": "SELECT title, posted_at",
        "mark": null
      },
      {
        "text": "FROM news",
        "mark": null
      },
      {
        "text": "ORDER BY posted_at",
        "mark": "del"
      },
      {
        "text": "ORDER BY posted_at DESC",
        "mark": "add"
      },
      {
        "text": "LIMIT 3;",
        "mark": null
      }
    ],
    "dayNote": "バックエンドの毎日では、画面の裏でデータベースから何をどの順で取り出すかを決め、正しいものだけを返します。"
  },
  "infrastructure": {
    "id": "infrastructure",
    "kicker": "インフラの仕事",
    "headline": "お店のサイトに、警告が出た",
    "ask": "10月14日の朝から、お店のサイトを開くとブラウザが警告を出します。サイトの身分証にあたる「証明書」を見てみました。原因の行はどれ？",
    "pickLabel": "サイトの証明書。原因の行を選ぶ",
    "picksHead": [
      "証明書の中身"
    ],
    "picks": [
      {
        "text": "発行先\tshop.example.jp",
        "ok": false,
        "say": "サイトの住所と同じ名前なので、ここは合っています。ここがずれていても警告は出ますが、今回は一致しています。"
      },
      {
        "text": "発行元\tLet's Encrypt",
        "ok": false,
        "say": "広く使われている発行元で、ブラウザも信頼しています。ここは問題ありません。"
      },
      {
        "text": "有効期限\t2026-10-13 まで",
        "ok": true,
        "say": "正解。昨日で期限が切れています。ブラウザは期限切れの証明書を信用せず、訪れた人に警告を出します。"
      }
    ],
    "picksTail": [],
    "fixAsk": "では、期限切れを繰り返さないようにするには？",
    "fixes": [
      {
        "label": "期限の長い証明書を買い、次も手で入れ替える",
        "ok": false,
        "say": "今回は直りますが、手で入れ替えるのを忘れれば、また同じことが起きます。証明書の有効期間の上限は段階的に短くなるので、手で入れ替える回数も、忘れる危険も増えていきます。"
      },
      {
        "label": "訪れた人に「警告は無視して進んでください」と案内する",
        "ok": false,
        "say": "偽のサイトでも同じ警告が出ます。無視する癖をつけさせると、利用者を危ない目にあわせます。"
      },
      {
        "label": "証明書を入れ替え、自動更新と、止まったときの見張りを付ける",
        "ok": true,
        "say": "正解。まず新しい証明書で今の警告を消し、次からは自動で更新します。更新の仕組みが黙って止まることもあるので、期限が近づくと知らせる見張りも付けます。"
      }
    ],
    "demo": {
      "kind": "browser",
      "url": "shop.example.jp",
      "before": {
        "bar": "保護されていない通信",
        "title": "この接続ではプライバシーが保護されません",
        "code": "NET::ERR_CERT_DATE_INVALID"
      },
      "after": {
        "bar": "保護された通信",
        "title": "本日のおすすめ",
        "text": "予約のページも、いつもどおり開けます"
      }
    },
    "diff": [
      {
        "text": "証明書\t期限切れ (10/13まで)",
        "mark": "del"
      },
      {
        "text": "証明書\t新しいものに入れ替え",
        "mark": "add"
      },
      {
        "text": "更新\t手で (90日ごと)",
        "mark": "del"
      },
      {
        "text": "更新\t自動 (期限の前に)",
        "mark": "add"
      },
      {
        "text": "見張り\t期限が近いと知らせる",
        "mark": "add"
      }
    ],
    "dayNote": "インフラの毎日では、証明書の期限やディスクの空きなど、放っておくと切れたり溢れたりするものを先回りして見張ります。"
  },
  "ai": {
    "id": "ai",
    "kicker": "AIエンジニアの仕事",
    "headline": "AIの答えで、お客さんが休みの日に来た",
    "ask": "お客さんから「問い合わせAIに水曜休みと聞いたのに、火曜に行ったら閉まっていました」と連絡が来ました。AIが答える前に読んだ資料は3つ。日付と中身を読んで、原因の資料を選んでください。",
    "pickLabel": "AIが答える前に読んだ資料。原因を選ぶ",
    "picksHead": [
      "AIが読んだ資料"
    ],
    "picks": [
      {
        "text": "改定のお知らせ  2025年3月\n  4月から定休日を火曜日に改めます",
        "ok": false,
        "say": "これは新しく正しい情報です。AIの答えの水曜日は、この資料とは食い違っています。原因は、古いまま残っている資料のほうです。"
      },
      {
        "text": "よくある質問  2026年9月\n  お休みの日は、店舗案内をご覧ください",
        "ok": false,
        "say": "新しい資料ですが、曜日は書かれていません。店舗案内を見るように書いてあるだけです。店舗案内が古いままだと、AIがそちらの曜日を使うおそれがあります。"
      },
      {
        "text": "店舗案内  2023年4月版\n  定休日は水曜日です",
        "ok": true,
        "say": "正解。2023年の古い店舗案内に、AIの答えと同じ「水曜日」が残っています。新しいお知らせと食い違う古い資料が、答えを狂わせています。"
      }
    ],
    "picksTail": [],
    "fixAsk": "では、資料の食い違いをなくすには？",
    "fixes": [
      {
        "label": "もっと新しく賢いAIのモデルに替える",
        "ok": false,
        "say": "モデルを替えても、食い違う資料はそのまま残ります。日付を読んで正しく答える場合もありますが、次の質問でまた迷うおそれがあります。元の古い資料を直すほうが確かです。"
      },
      {
        "label": "古い店舗案内を今の内容に直し、同じ質問で答えを確かめ直す",
        "ok": true,
        "say": "正解。古い資料を直して食い違いをなくし、同じ質問をもう一度して、答えが火曜日になったかを確かめます。資料を替えただけで終わらせず、直ったことを自分の目で見るまでが仕事です。"
      },
      {
        "label": "AIへの指示に「正確に答えてください」と書き足す",
        "ok": false,
        "say": "頼み方を変えても、食い違う資料が残っている限り、また古い曜日を答えるおそれがあります。"
      }
    ],
    "demo": {
      "kind": "chat",
      "question": "定休日はいつですか？",
      "before": {
        "answer": "水曜日です。",
        "source": "3件"
      },
      "after": {
        "answer": "火曜日です。2025年4月から変わりました。",
        "source": "店舗案内 2026年版、改定のお知らせ 2025年3月"
      }
    },
    "diff": [
      {
        "text": "店舗案内  2023年4月版  定休日 水曜",
        "mark": "del"
      },
      {
        "text": "店舗案内  2026年版  定休日 火曜",
        "mark": "add"
      },
      {
        "text": "確かめ  定休日は？ → 火曜日",
        "mark": "add"
      }
    ],
    "dayNote": "AIエンジニアの毎日では、AIの答えが外れたら、何を読んでどう答えたかをたどり、原因が資料か探し方か指示かを絞り込みます。"
  },
  "data": {
    "id": "data",
    "kicker": "データ分析の仕事",
    "headline": "今月の売上が、半分に見える",
    "ask": "10月14日の閉店後、店長から「今月の売上が先月の半分です。何があったの？」と連絡が来ました。その日のうちに、本当かどうか確かめたい。売上のグラフの設定のうち、おかしいのはどれ？",
    "pickLabel": "売上のグラフの設定。原因の行を選ぶ",
    "picksHead": [
      "グラフの設定"
    ],
    "picks": [
      {
        "text": "見る数字\t売上の合計",
        "ok": false,
        "say": "売上を合計で見るのは普通のことです。問題は、何と何の合計を並べているかです。"
      },
      {
        "text": "今月\t10/1〜10/14 (14日)",
        "ok": false,
        "say": "今日までの数字を見るのは正しいことです。月の途中でも、様子は知りたいものです。問題は、それを何と並べているかです。"
      },
      {
        "text": "比べる相手\t9/1〜9/30 (30日)",
        "ok": true,
        "say": "正解。14日分の売上を、30日分の売上と比べています。日数が倍ちがうので、売上が落ちていなくても半分に見えます。"
      }
    ],
    "picksTail": [],
    "fixAsk": "では、どう直す？",
    "fixes": [
      {
        "label": "先月も同じ14日間 (9/1〜9/14) にそろえて比べる",
        "ok": true,
        "say": "正解。同じ14日間で比べると、140万円と146万円で4%増えていました。この14日を見る限り、売上は落ちていません。曜日の並びまでそろえると、さらに確かになります。"
      },
      {
        "label": "月末まで待ってから比べる",
        "ok": false,
        "say": "月末まで待つと、その日のうちに判断できません。それに9月は30日、10月は31日で、日数もそろいません。"
      },
      {
        "label": "棒グラフを折れ線グラフに変える",
        "ok": false,
        "say": "見た目が変わるだけで、14日と30日を比べている中身は同じです。グラフの形より先に、何と何を比べるかを決めます。"
      }
    ],
    "demo": {
      "kind": "bars",
      "title": "売上の比べ",
      "unit": "万円",
      "before": {
        "badge": "-51%",
        "bars": [
          {
            "label": "9月 (30日)",
            "value": 300
          },
          {
            "label": "10月 (14日)",
            "value": 146
          }
        ]
      },
      "after": {
        "badge": "+4%",
        "bars": [
          {
            "label": "9/1〜14",
            "value": 140
          },
          {
            "label": "10/1〜14",
            "value": 146
          }
        ]
      }
    },
    "diff": [
      {
        "text": "比べる相手\t9/1〜9/30 (30日)",
        "mark": "del"
      },
      {
        "text": "比べる相手\t9/1〜9/14 (14日)",
        "mark": "add"
      },
      {
        "text": "変化\t-51%",
        "mark": "del"
      },
      {
        "text": "変化\t+4%",
        "mark": "add"
      }
    ],
    "dayNote": "データ分析の毎日では、数字が大きく動いたとき、まず比べ方がそろっているかを確かめてから、何が起きたのかを読み解きます。"
  },
  "security": {
    "id": "security",
    "kicker": "セキュリティの仕事",
    "headline": "管理者のログインが、1分に480回失敗",
    "ask": "お店のサイトのログインの記録に、見慣れない動きがあります。守る側として、攻撃が疑われる行はどれ？",
    "pickLabel": "ログインの記録。攻撃が疑われる行を選ぶ",
    "picksHead": [],
    "picks": [
      {
        "text": "10:02:11\tsato   成功\n相手 203.0.113.5",
        "ok": false,
        "say": "1回で入れていて、続く失敗もありません。この行だけでは、怪しい点は見当たりません。"
      },
      {
        "text": "10:02:14\tadmin  失敗 ×480\n相手 198.51.100.7\n1分間で",
        "ok": true,
        "say": "正解。同じ相手から、管理者のアカウントに1分で480回の失敗。人の手では打てない速さで、機械的にパスワードを試されている疑いが強いです。"
      },
      {
        "text": "10:03:40\ttanaka 失敗\n相手 192.0.2.44",
        "ok": false,
        "say": "1回だけの失敗は、打ち間違いでよく起きます。続いていないので、これだけで攻撃とは言えません。"
      }
    ],
    "picksTail": [],
    "fixAsk": "では、この先も守り続けるには？",
    "fixes": [
      {
        "label": "この相手の住所 (IPアドレス) だけを止める",
        "ok": false,
        "say": "今の分は止まるので、応急の手当てにはなります。ただ相手は住所を替えられるので、守り続けるには、アカウントへの失敗の数で絞る仕組みが要ります。"
      },
      {
        "label": "ログイン画面の住所を変えて、見つかりにくくする",
        "ok": false,
        "say": "隠しても、いずれ見つけられます。隠すことは、守りの代わりにはなりません。"
      },
      {
        "label": "失敗が続いたら待ち時間を入れ、管理者には2段階認証を必ず使ってもらう",
        "ok": true,
        "say": "正解。待ち時間で続けて試せないようにし、2段階認証 (パスワードのほかに、もう1つ本人の確認を求める仕組み) で、パスワードだけでは入れないようにします。待ち時間が長すぎると本人も入れなくなるので、少しずつ延ばす形がよく使われます。"
      }
    ],
    "demo": {
      "kind": "meter",
      "title": "admin のパスワードを1分に試せる回数",
      "before": {
        "value": 480,
        "max": 480,
        "note": "止める仕組みがなく、記録では1分に480回試された",
        "tag": "危ない"
      },
      "after": {
        "value": 5,
        "max": 480,
        "note": "設定した上限。5回失敗すると1分待ち、続けば待ちを延ばす。パスワードだけでは入れない",
        "tag": "制限あり"
      }
    },
    "diff": [
      {
        "text": "失敗の制限\tなし",
        "mark": "del"
      },
      {
        "text": "失敗の制限\t5回で1分待ち (続けば延ばす)",
        "mark": "add"
      },
      {
        "text": "2段階認証\t管理者は任意",
        "mark": "del"
      },
      {
        "text": "2段階認証\t管理者は必須",
        "mark": "add"
      }
    ],
    "dayNote": "セキュリティの毎日では、記録から怪しい動きを見つけ、入られにくい仕組みを先に整えます。"
  },
  "web-production": {
    "id": "web-production",
    "kicker": "マークアップの仕事",
    "headline": "トップの画像が、縦に伸びた",
    "ask": "お店のサイトの一番上の画像が、スマホで見ると縦に伸びて、丸いロゴが縦長の楕円に、フクロウが細長くなっています。画像の見た目を決める指定 (CSS) のうち、原因の行はどれ？",
    "pickLabel": "画像の CSS。原因の行を選ぶ",
    "picksHead": [
      ".hero img {\n  /* 一番上の画像 */"
    ],
    "picks": [
      {
        "text": "  width: 100%;\n    /* 幅は入れ物に合わせて変わる */",
        "ok": false,
        "say": "画像の幅を、入れ物の幅に合わせる行です。狭い画面で、画像がはみ出さないための大事な指定です。"
      },
      {
        "text": "  height: 220px;\n    /* 高さは 220px に決め打ち */",
        "ok": true,
        "say": "正解。幅は画面に合わせて縮むのに、高さは220pxのままです。縦と横の比が崩れて、絵が縦に伸びます。"
      },
      {
        "text": "  border-radius: 12px;\n    /* 角の丸み */",
        "ok": false,
        "say": "角を丸くするだけの行です。形の伸び縮みには関わりません。"
      }
    ],
    "picksTail": [
      "}"
    ],
    "fixAsk": "では、どんな幅の画面でも形が崩れないようにするには？",
    "fixes": [
      {
        "label": "高さを150pxに縮める",
        "ok": false,
        "say": "ある幅のスマホでは近い形になりますが、画面の幅が変わればまた伸び縮みします。幅に合わせて高さが決まる書き方があります。"
      },
      {
        "label": "高さを auto にして、縦と横の比を保つ",
        "ok": true,
        "say": "正解。height を auto にすると、幅に合わせて高さも縮み、元の縦と横の比のまま映ります。"
      },
      {
        "label": "幅も300pxに決める",
        "ok": false,
        "say": "高さが220pxのままなので、幅を300pxにしても絵は縦に伸びたままです。そのうえ、どんな画面でも300pxに決まってしまいます。"
      }
    ],
    "demo": {
      "kind": "image",
      "src": "/assets/jd-trial-shop-hero.webp",
      "alt": "お店のトップの画像",
      "before": {
        "height": "220px"
      },
      "after": {
        "height": "auto"
      },
      "frameMaxWidth": "300px"
    },
    "diff": [
      {
        "text": ".hero img {",
        "mark": null
      },
      {
        "text": "  width: 100%;",
        "mark": null
      },
      {
        "text": "  height: 220px;",
        "mark": "del"
      },
      {
        "text": "  height: auto;",
        "mark": "add"
      },
      {
        "text": "  border-radius: 12px;",
        "mark": null
      },
      {
        "text": "}",
        "mark": null
      }
    ],
    "dayNote": "マークアップの毎日では、見本どおりに組んだページをいろいろな幅の画面で開き、崩れた所を1つずつ直します。"
  },
  "ai-work": {
    "id": "ai-work",
    "kicker": "業務自動化の仕事",
    "headline": "急ぎの連絡なのに、通知が鳴らない",
    "ask": "問い合わせを自動で表に記録し、急ぎのものだけ担当に知らせる仕組みです。3の条件は、ほかの通知でも使っているので変えない決まりです。ところが、急ぎの問い合わせが届いても通知が来ませんでした。原因はどの手順？",
    "pickLabel": "自動化の手順。原因の手順を選ぶ",
    "picksHead": [],
    "picks": [
      {
        "text": "1  問い合わせが届いたら始める",
        "ok": false,
        "say": "ここは毎回ちゃんと動いていて、表にも行が足されています。止まっているのは通知だけです。"
      },
      {
        "text": "2  AIに「急ぎかどうか教えて」と聞く",
        "ok": true,
        "say": "正解。答え方を決めずに聞いたので、AIは「至急の対応が必要です」と文で返しました。3は「急ぎ」という言葉を待っているので、一致せず通知が飛びません。"
      },
      {
        "text": "3  答えがぴったり「急ぎ」なら担当に通知する",
        "ok": false,
        "say": "3は変えない決まりで、答えがぴったり「急ぎ」のときだけ動きます。合わせるべきは、答え方を決めずにAIに聞いている2のほうです。"
      }
    ],
    "picksTail": [
      "4  表に1行足す"
    ],
    "fixAsk": "では、別の言い方でも漏れないように、どう直す？",
    "fixes": [
      {
        "label": "答えを「急ぎ」か「ふつう」の一語に決め、それ以外が返ったら人に回す",
        "ok": true,
        "say": "正解。答えの形を先に決めておくと、3がそのまま受け取れます。決まった形で返らなかったときに人へ回す逃げ道も、自動化には欠かせません。"
      },
      {
        "label": "2の聞き方に「短く答えて」と足す",
        "ok": false,
        "say": "短くはなっても「至急です」「急ぎです」のように揺れます。言葉をぴったり決めないと、3は受け取れません。"
      },
      {
        "label": "急ぎの問い合わせは、電話でしてもらうよう案内する",
        "ok": false,
        "say": "自動化をやめて、手作業に戻しています。問い合わせる人に手間を回さず、仕組みの側で受け止めます。"
      }
    ],
    "demo": {
      "kind": "table",
      "columns": [
        "問い合わせ",
        "AIの答え",
        "通知"
      ],
      "before": [
        [
          "サイトから予約できません。今夜の予約です",
          "至急の対応が必要です",
          "届かず"
        ],
        [
          "領収書の宛名を変えたいです",
          "ふつう",
          "不要"
        ]
      ],
      "after": [
        [
          "サイトから予約できません。今夜の予約です",
          "急ぎ",
          "送った"
        ],
        [
          "領収書の宛名を変えたいです",
          "ふつう",
          "不要"
        ]
      ],
      "highlightRow": 0
    },
    "diff": [
      {
        "text": "2  AIに「急ぎかどうか教えて」と聞く",
        "mark": "del"
      },
      {
        "text": "2  AIに「急ぎ」か「ふつう」で答えさせる",
        "mark": "add"
      },
      {
        "text": "   ほかの答えなら人に回す",
        "mark": "add"
      },
      {
        "text": "3  答えがぴったり「急ぎ」なら担当に通知する",
        "mark": null
      }
    ],
    "dayNote": "業務自動化の毎日では、つないだツールやAIのあいだで言葉や形が食い違って止まる所を見つけ、決まった形でつなぎ直します。"
  },
};
